import React, { Component } from 'react';
import { format } from 'date-fns';
import _ from 'lodash';
import { isUserAdmin, isUserOwner } from '../../helpers';
import { withRouter } from '../../helpers/withRouter';
import { MARKETING_MODAL, PAYROLL_MODAL, PROJECT_MODAL } from '../../redux/reducers/modal';
import MarketingStatsModal from '../blocks/marketing/MarketingStatsModal';
import ReduxModal from '../blocks/modal/ReduxModal';
import MainNav from '../blocks/navigation/MainNav';
import PayrollGeneratorModal from '../blocks/payroll/PayrollGeneratorModal';
import ProjectsContent from '../blocks/projects/ProjectsContent';
import ProjectsModal from '../blocks/projects/ProjectsModal';
import type { ProjectProps } from '../../containers/Projects';
import type { Project, Router } from '../../types';

type State = {
  modal: {
    edit: boolean,
    saveButtonText: string,
    title: string,
    project: Project | null,
  },
  searchForm: {
    year: number,
    month: number,
  },
  searchTriggered: boolean,
}

type Props = ProjectProps & {
  router: Router,
}

class Projects extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { location } = this.props.router;
    const params = new URLSearchParams(location.search);
    const monthIndex = _.toNumber(params.get('month'));
    const yearIndex = _.toNumber(params.get('year'));

    const month = monthIndex > 0 ? monthIndex : (new Date().getMonth() + 1);
    const year = yearIndex > 0 ? yearIndex : new Date().getFullYear();

    this.state = {
      modal: {
        edit: false,
        saveButtonText: '',
        title: '',
        project: null,
      },
      searchForm: { year, month },
      searchTriggered: true,
    };
  }

  componentDidMount() {
    this.props.fetchCategories();

    if (this.state.searchForm.year && this.state.searchForm.month) {
      this.searchProjects();
    } else {
      this.fetchProjectsDataForCurrentMonth();
    }
  }

  componentDidUpdate = (prevProps: Props) => {
    const {
      location: { search },
    } = this.props.router;
    const searchChanged = prevProps.router.location.search !== search;

    if (searchChanged) {
      if (search.length > 0) {
        this.searchProjects();
      } else {
        this.fetchProjectsDataForCurrentMonth();
      }
    }
  };

  handleModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: PROJECT_MODAL });
    this.setState({
      modal: {
        edit: false,
        saveButtonText: '',
        title: '',
        project: null,
      },
    });
  };

  handleSelectMonthChange = (month: number) => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, month },
    });
  };

  handleSelectYearChange = (year: number) => {
    const { searchForm } = this.state;
    this.setState({
      searchForm: { ...searchForm, year },
    });
  };

  searchProjects = () => {
    const {
      onProjectSearch, onDatesChanged,
    } = this.props;
    const { searchForm: { year, month } } = this.state;
    this.setState({ searchTriggered: true });

    if (onProjectSearch) {
      onProjectSearch(year, month);
    } else {
      onDatesChanged(year, month);
    }
  };

  handleSearchProjectsSubmit = () => {
    const { navigate } = this.props.router;
    const { searchForm: { year, month } } = this.state;
    navigate(`/projects?year=${year}&month=${month}`);
  };

  handleSearchProjectsReset = () => {
    const { navigate } = this.props.router;
    this.setState({
      searchTriggered: false,
      searchForm: {
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
      },
    });
    navigate('/projects');
  };

  handleOpenAddProjectModal = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: PROJECT_MODAL });
    this.setState({
      modal: {
        edit: false,
        saveButtonText: 'Save new project',
        title: 'Add project',
        project: null,
      },
    });
  };

  handleOpenEditProjectModal = (project: Project) => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: PROJECT_MODAL });
    this.setState({
      modal: {
        edit: true,
        saveButtonText: 'Save',
        title: 'Save project',
        project,
      },
    });
  };

  handleProjectDelete = (projectId: number) => {
    const { onRemoveProject } = this.props;
    const { searchTriggered } = this.state;
    const afterSubmitActions = () => {
      if (searchTriggered) this.searchProjects();
      this.handleModalClose();
    };
    this.disableProjectsModalButtons();
    onRemoveProject(projectId)
      .then(afterSubmitActions)
      .catch(this.handleModalClose);
  };

  handleProjectSubmit = (formData: any) => {
    const { onAddProject, onEditProject } = this.props;
    const { modal: { edit }, searchTriggered } = this.state;
    this.disableProjectsModalButtons();
    const afterEditSubmitActions = () => {
      if (searchTriggered) this.searchProjects();
      this.handleModalClose();
    };
    const afterAddSubmitActions = () => {
      this.handleModalClose();
      this.handleSearchProjectsReset();
    };

    if (edit) {
      onEditProject(formData).then(afterEditSubmitActions);
    } else {
      onAddProject(formData).then(afterAddSubmitActions);
    }
  };

  handleRowClick = (project: Project) => {
    const { navigate, location } = this.props.router;
    navigate(`/projects/${project.id}${location.search}`);
  };

  disableProjectsModalButtons = () => {
    const { onUpdateModal } = this.props;
    onUpdateModal({ id: PROJECT_MODAL, disableButtons: true });
  };

  fetchProjectsDataForCurrentMonth = () => {
    const { onProjectSearch } = this.props;
    const searchDate = new Date();
    onProjectSearch(format(searchDate, 'yyyy'), format(searchDate, 'MM'));
  };

  handlePayrollGeneratorClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: PAYROLL_MODAL });
  };

  handlePayrollModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: PAYROLL_MODAL });
  };

  handleMarketingStatsClick = () => {
    const { onOpenModal } = this.props;
    onOpenModal({ id: MARKETING_MODAL });
  };

  handleMarketingModalClose = () => {
    const { onCloseModal } = this.props;
    onCloseModal({ id: MARKETING_MODAL });
  };

  render(): React.ReactNode {
    const { modal, searchForm, searchTriggered } = this.state;
    const { categories, currentUser, projects } = this.props;
    const { navigate } = this.props.router;
    const isAdmin = isUserAdmin(currentUser);
    const isOwner = isUserOwner(currentUser);

    return (
      <div className="container-view view-wrapper projects-view">
        <MainNav
          currentUser={currentUser}
          navigate={navigate}
          onPayrollGeneratorClick={this.handlePayrollGeneratorClick}
          onMarketingStatsClick={this.handleMarketingStatsClick}
        />
        <ProjectsContent
          projects={projects.projectsToDisplay}
          selectedYear={searchForm.year}
          selectedMonth={searchForm.month}
          onOpenAddProjectModal={this.handleOpenAddProjectModal}
          onOpenEditProjectModal={this.handleOpenEditProjectModal}
          onSelectMonthChange={this.handleSelectMonthChange}
          onSelectYearChange={this.handleSelectYearChange}
          onSearchProjectsSubmit={this.handleSearchProjectsSubmit}
          onSearchProjectsReset={this.handleSearchProjectsReset}
          onRowClick={this.handleRowClick}
          searchTriggered={searchTriggered}
          isAdmin={isAdmin}
          isOwner={isOwner}
        />
        <ReduxModal id={PROJECT_MODAL}>
          <ProjectsModal
            title={modal.title}
            edit={modal.edit}
            project={modal.project}
            saveButtonText={modal.saveButtonText}
            onProjectSubmit={this.handleProjectSubmit}
            onProjectDelete={this.handleProjectDelete}
            onCloseClick={this.handleModalClose}
            allActiveCategories={Array.isArray(categories) ? categories : []}
          />
        </ReduxModal>
        <ReduxModal id={PAYROLL_MODAL}>
            <PayrollGeneratorModal
              onCloseModal={this.handlePayrollModalClose}
            />
          </ReduxModal>
          <ReduxModal id={MARKETING_MODAL}>
            <MarketingStatsModal
              onCloseModal={this.handleMarketingModalClose}
            />
          </ReduxModal>
      </div>
    );
  }
}

const ConnectedProjects = withRouter(Projects as any);

export default ConnectedProjects;
